/*
 * Copyright (C) 2019-2022 Corvalent Corporation - All Rights Reserved
 * This software is licensed program property and is Proprietary and Confidential.
 * Unauthorized copying of this file via any medium is strictly prohibited unless
 * prior written permission has been obtained by Corvalent Corporation.
 * In addition, the look and feel of this software as well as the user interface
 * design is also Copyright ©.
 * Notice: This software is not open source.
 * This software is subject to the Corvalent Software License Agreement.
 * You should have received a copy of this license with the software.
 * If not, please write to: sales@corvalent.com
 * All information contained herein is, and remains, the property of
 * Corvalent Corporation and its suppliers, if any. This software may also
 * be covered by U.S. and Foreign Patents, patents in progress, and are
 * protected by trade secret or copyright law
 */

import React from 'react';
import { StyledEngineProvider } from '@mui/material/styles';
import { Router } from 'react-router-dom';
import { Provider } from 'react-redux';

import { Store } from '@redux/store';
import Routes from '@routes';
import HooksProvider from '@hooks';

// Import FilePond
import * as FilePond from 'filepond';
import 'filepond/dist/filepond.min.css';
// Import the plugin styles
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';
// Import the plugin code
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';

import { throttle } from 'lodash';
import { forceCheck } from 'react-lazyload';

import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from './services/queryClient';

import AppWrapper from './AppWrapper';
import 'bootstrap/dist/css/bootstrap.min.css';
import GeneralContext from './context/GeneralContext';
import MQTTContext from './context/MQTTContext';
import ConfirmationContext from './context/ConfirmationContext';
import LocationsCacheContext from './context/LocationsCacheContext';
import MapPopUpsContext from './context/MapPopUpsContext';
import ModalFactoryContext from './context/ModalFactoryContext';
import AssetFormContext from './context/AssetFormContext';
import SensorReadingsContext from './context/SensorReadingsContext';
import NotificationsContext from './context/NotificationsContext';
import FilterContext from './context/FilterContext';
import ActuatorContext from './context/ActuatorContext';
import customHistory from './routes/history';

// Register the plugin
FilePond.registerPlugin(
  FilePondPluginFileValidateType,
  FilePondPluginFileValidateSize,
  FilePondPluginImagePreview,
);

document.addEventListener(
  'scroll',
  throttle(() => forceCheck(), 16),
  true,
);

const App = () => {
  return (
    <StyledEngineProvider injectFirst>
      <QueryClientProvider client={queryClient}>
        <Router history={customHistory}>
          <Provider store={Store}>
            <AppWrapper>
              <FilterContext>
                <GeneralContext>
                  <SensorReadingsContext>
                    <NotificationsContext>
                      <LocationsCacheContext>
                        <MapPopUpsContext>
                          <MQTTContext>
                            <HooksProvider>
                              <ConfirmationContext>
                                <ModalFactoryContext>
                                  <AssetFormContext>
                                    <ActuatorContext>
                                      <Routes />
                                    </ActuatorContext>
                                  </AssetFormContext>
                                </ModalFactoryContext>
                              </ConfirmationContext>
                            </HooksProvider>
                          </MQTTContext>
                        </MapPopUpsContext>
                      </LocationsCacheContext>
                    </NotificationsContext>
                  </SensorReadingsContext>
                </GeneralContext>
              </FilterContext>
            </AppWrapper>
          </Provider>
        </Router>
        <ReactQueryDevtools />
      </QueryClientProvider>
    </StyledEngineProvider>
  );
};

export default App;
